import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get stored token
const getToken = () => localStorage.getItem('hiviz-token');

// Create axios instance with auth
const apiClient = axios.create({
  baseURL: API,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============= AUTH API =============

export const authAPI = {
  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) {
      localStorage.setItem('hiviz-token', response.data.access_token);
    }
    return response.data;
  },

  register: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    if (response.data.access_token) {
      localStorage.setItem('hiviz-token', response.data.access_token);
    }
    return response.data;
  },

  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('hiviz-token');
  }
};

// ============= PRODUCTS API =============

export const productsAPI = {
  getAll: async (category = null) => {
    const params = category && category !== 'all' ? { category } : {};
    const response = await axios.get(`${API}/products`, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API}/products/${id}`);
    return response.data;
  },

  create: async (productData) => {
    const response = await apiClient.post('/products', productData);
    return response.data;
  },

  update: async (id, productData) => {
    const response = await apiClient.put(`/products/${id}`, productData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/products/${id}`);
    return response.data;
  }
};

// ============= ORDERS API =============

export const ordersAPI = {
  create: async (orderData) => {
    const response = await apiClient.post('/orders', orderData);
    return response.data;
  },

  createGuest: async (orderData) => {
    const response = await axios.post(`${API}/orders/guest`, orderData);
    return response.data;
  },

  getMyOrders: async () => {
    const response = await apiClient.get('/orders');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/orders/${id}`);
    return response.data;
  }
};

// ============= DISCOUNT API =============

export const discountAPI = {
  getTiers: async () => {
    const response = await axios.get(`${API}/discounts/tiers`);
    return response.data;
  },

  calculate: async (quantity, unitPrice) => {
    const response = await axios.post(`${API}/discounts/calculate`, null, {
      params: { quantity, unit_price: unitPrice }
    });
    return response.data;
  }
};

// ============= ADMIN API =============

export const adminAPI = {
  getAllOrders: async () => {
    const response = await apiClient.get('/admin/orders');
    return response.data;
  },

  updateOrderStatus: async (orderId, status) => {
    const response = await apiClient.patch(`/admin/orders/${orderId}/status`, null, {
      params: { status }
    });
    return response.data;
  },

  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  syncProductStock: async () => {
    const response = await apiClient.post('/admin/update-product-stock');
    return response.data;
  },

  syncProductGallery: async () => {
    const response = await apiClient.post('/admin/update-product-gallery');
    return response.data;
  }
};

// ============= CATEGORIES API =============

export const categoriesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/categories`);
    return response.data;
  }
};

export default {
  auth: authAPI,
  products: productsAPI,
  orders: ordersAPI,
  discount: discountAPI,
  admin: adminAPI,
  categories: categoriesAPI
};
